import { IS_PRODUCTION } from '@solinkcorp/settings/environment';

/** Statuspage indicator levels: https://status.solinkcloud.com/api/v2/ */
export type OutageIndicator = 'none' | 'minor' | 'major' | 'critical';

/** Non-production `?forceOutage=` preview modes for the major-outage page. */
export type ForceOutageMode = 'major' | 'recover' | 'restored';

/** True when the app should block behind the major-outage page. */
export function isBlockingOutage(indicator: OutageIndicator | string | undefined): boolean {
  return indicator === 'major' || indicator === 'critical';
}

/** True when a previously blocking outage has cleared enough to reload. */
export function isOutageCleared(indicator: OutageIndicator | string | undefined): boolean {
  return indicator === 'none' || indicator === 'minor';
}

/**
 * Parse the non-production `?forceOutage=` preview query param.
 * Returns null in production or when the param is missing/invalid.
 */
export function getForceOutageMode(): ForceOutageMode | null {
  if (IS_PRODUCTION) return null;
  try {
    const value = new URLSearchParams(window.location.search).get('forceOutage');
    if (value === 'major' || value === 'recover' || value === 'restored') return value;
  } catch {
    // ignore
  }
  return null;
}

/** True when a non-production forceOutage preview should show the outage page. */
export function shouldForceMajorOutage(): boolean {
  return getForceOutageMode() !== null;
}

/**
 * Allow only http(s) URLs for status-page links. Returns `fallback` for
 * missing/invalid values or unsafe schemes (e.g. `javascript:`).
 */
export function toSafeHttpUrl(url: string | undefined, fallback: string): string {
  if (!url) return fallback;
  try {
    const parsed = new URL(url);
    if (parsed.protocol === 'http:' || parsed.protocol === 'https:') {
      return parsed.href;
    }
  } catch {
    // ignore invalid URLs
  }
  return fallback;
}
